import { useState, useEffect, useMemo } from "react";
import * as api from "../../apiService";

const BU_STYLE = {
  AMI: { label: "AMI", bg: "#eff6ff", color: "#1d4ed8", border: "#bfdbfe" },
  AMT: { label: "AMT", bg: "#f0fdfa", color: "#0d9488", border: "#99f6e4" },
};

function daysAgo(dateStr) {
  if (!dateStr) return null;
  return Math.floor((Date.now() - new Date(dateStr)) / 86_400_000);
}


function fmtAed(val) {
  const n = Number(val);
  if (!n || n === 0) return <span style={{ color: "#9ca3af" }}>—</span>;
  return `AED ${n.toLocaleString("en-AE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-AE", { day: "2-digit", month: "short", year: "numeric" });
}

function tlExpiryStyle(iso) {
  if (!iso) return {};
  const days = Math.floor((new Date(iso) - Date.now()) / 86_400_000);
  if (days < 0)  return { color: "#b91c1c", fontWeight: 600 };
  if (days < 30) return { color: "#b45309", fontWeight: 600 };
  return {};
}

function lastInvoiceLabel(days) {
  if (days === null) return { text: "—", color: "#9ca3af" };
  if (days <= 30)  return { text: `${days}d ago`, color: "#16a34a" };
  if (days <= 60)  return { text: `${days}d ago`, color: "#16a34a" };
  if (days <= 120) return { text: `${days}d ago`, color: "#b45309" };
  return { text: `${days}d ago`, color: "#b91c1c" };
}

// ── Customer table rows ───────────────────────────────────────────────────────
function CustomerTable({ customers, search, onSelectAccount, emptyMsg }) {
  const filtered = customers.filter(c =>
    !search || c.ledgerName.toLowerCase().includes(search.toLowerCase())
  );
  if (filtered.length === 0) {
    return (
      <div style={{ textAlign: "center", color: "#9ca3af", padding: "12px 0", fontSize: 12 }}>
        {emptyMsg || "No customers found."}
      </div>
    );
  }
  return (
    <div className="table-wrap">
      <table className="data-table">
        <thead>
          <tr>
            <th>Customer (Tally Ledger)</th>
            <th style={{ textAlign: "right" }}>Outstanding</th>
            <th style={{ textAlign: "right" }}>Credit Limit</th>
            <th style={{ textAlign: "center" }}>Credit Days</th>
            <th style={{ textAlign: "center" }}>Last Invoice</th>
            <th style={{ textAlign: "center" }}>CRM Profile</th>
          </tr>
        </thead>
        <tbody>
          {filtered.map(c => {
            const days = daysAgo(c.lastVoucherDate);
            const lbl  = lastInvoiceLabel(days);
            const effectiveLimit = c.creditLimit ?? c.tallyCreditLimit;
            return (
              <tr key={c.ledgerName}
                style={{ cursor: c.crmAccountId ? "pointer" : "default" }}
                onClick={() => c.crmAccountId && onSelectAccount(c.crmAccountId)}>
                <td className="fw-600">{c.ledgerName}</td>
                <td style={{ textAlign: "right", fontSize: 12 }}>{fmtAed(c.outstanding)}</td>
                <td style={{ textAlign: "right", fontSize: 12 }}>{fmtAed(effectiveLimit)}</td>
                <td style={{ textAlign: "center", fontSize: 12, color: c.creditTerms ? "#374151" : "#9ca3af" }}>
                  {c.creditTerms || "—"}
                </td>
                <td style={{ textAlign: "center", fontSize: 12, color: lbl.color, fontWeight: days > 60 ? 600 : 400 }}>
                  {lbl.text}
                </td>
                <td style={{ textAlign: "center" }}>
                  {c.crmAccountId
                    ? <span style={{ fontSize: 11, color: "#1d4ed8", fontWeight: 600 }}>View Profile</span>
                    : <span style={{ fontSize: 11, color: "#9ca3af" }}>No profile</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── Collapsible activity section ──────────────────────────────────────────────
function ActivitySection({ title, count, accentColor, defaultOpen, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ marginBottom: 16 }}>
      <button onClick={() => setOpen(o => !o)} style={{
        display: "flex", alignItems: "center", gap: 8,
        background: "none", border: "none", cursor: "pointer",
        padding: "6px 0", width: "100%", textAlign: "left",
      }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: accentColor }}>{title}</span>
        <span style={{ fontSize: 11, color: accentColor, fontWeight: 400 }}>({count})</span>
        <span style={{ fontSize: 11, color: "#9ca3af", marginLeft: "auto" }}>{open ? "▲" : "▼"}</span>
      </button>
      {open && children}
    </div>
  );
}

// ── Customer table for one company tab ───────────────────────────────────────
function CompanySection({ companyData, search, onSelectAccount }) {
  const all = companyData.customers;

  const active     = all.filter(c => { const d = daysAgo(c.lastVoucherDate); return d !== null && d <= 60; });
  const inactive60 = all.filter(c => { const d = daysAgo(c.lastVoucherDate); return d !== null && d > 60 && d <= 120; });
  const inactive120= all.filter(c => { const d = daysAgo(c.lastVoucherDate); return d === null || d > 120; });

  return (
    <div>
      <ActivitySection title="Active" count={active.length} accentColor="#16a34a" defaultOpen={true}>
        <CustomerTable customers={active} search={search} onSelectAccount={onSelectAccount}
          emptyMsg="No active customers." />
      </ActivitySection>

      <ActivitySection title="Inactive · 60–120 days" count={inactive60.length} accentColor="#b45309" defaultOpen={false}>
        <CustomerTable customers={inactive60} search={search} onSelectAccount={onSelectAccount}
          emptyMsg="None in this range." />
      </ActivitySection>

      <ActivitySection title="Inactive · 120+ days" count={inactive120.length} accentColor="#b91c1c" defaultOpen={false}>
        <CustomerTable customers={inactive120} search={search} onSelectAccount={onSelectAccount}
          emptyMsg="None in this range." />
      </ActivitySection>
    </div>
  );
}

// ── Prospects tab (from crm.accounts) ────────────────────────────────────────
function ProspectsSection({ prospects, search, onSelectAccount }) {
  const filtered = prospects.filter(a =>
    !search || a.name?.toLowerCase().includes(search.toLowerCase())
      || a.assignedTo?.toLowerCase().includes(search.toLowerCase())
  );
  return (
    <div className="card" style={{ marginTop: 0 }}>
      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <th>Account Name</th>
              <th>Salesperson</th>
              <th style={{ textAlign: "center" }}>Trade Licence Expiry</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr>
                <td colSpan={3} style={{ textAlign: "center", color: "#9ca3af", padding: 32 }}>
                  No prospects found.
                </td>
              </tr>
            )}
            {filtered.map(a => (
              <tr key={a.id} style={{ cursor: "pointer" }} onClick={() => onSelectAccount(a.id)}>
                <td className="fw-600">{a.name}</td>
                <td style={{ fontSize: 12 }}>{a.assignedTo || <span className="text-muted">—</span>}</td>
                <td style={{ textAlign: "center", fontSize: 12, ...tlExpiryStyle(a.tradeLicenseExpiry) }}>
                  {fmtDate(a.tradeLicenseExpiry)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── AMI / AMT sub-tabs for one salesperson ────────────────────────────────────
function SalespersonPanel({ sp, search, onSelectAccount }) {
  const [activeBu, setActiveBu] = useState(
    sp.companies.length > 0 ? sp.companies[0].businessUnit : null
  );

  const activeCompany = sp.companies.find(c => c.businessUnit === activeBu);

  if (sp.companies.length === 0) {
    return (
      <div style={{ textAlign: "center", color: "#9ca3af", padding: 32 }}>
        No active groups assigned for this salesperson.
      </div>
    );
  }

  return (
    <div className="card" style={{ marginTop: 0 }}>
      {/* Company sub-tabs */}
      <div style={{ display: "flex", gap: 0, borderBottom: "1px solid #e5e7eb", marginBottom: 16 }}>
        {sp.companies.map(company => {
          const s = BU_STYLE[company.businessUnit] ?? { label: company.businessUnit, color: "#374151", border: "#e5e7eb" };
          const isActive = activeBu === company.businessUnit;
          const total      = company.customers.length;
          const activeCount= company.customers.filter(c => { const d = daysAgo(c.lastVoucherDate); return d !== null && d <= 60; }).length;
          return (
            <button key={company.companyId}
              onClick={() => setActiveBu(company.businessUnit)}
              style={{
                background: "none", border: "none", cursor: "pointer",
                padding: "8px 20px", fontSize: 13, fontWeight: 700, whiteSpace: "nowrap",
                color: isActive ? s.color : "#9ca3af",
                borderBottom: isActive ? `2px solid ${s.color}` : "2px solid transparent",
                marginBottom: -1,
              }}>
              {s.label}
              <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 400,
                color: isActive ? s.color : "#9ca3af" }}>
                {activeCount}/{total}
              </span>
            </button>
          );
        })}
      </div>

      {activeCompany && (
        <CompanySection
          companyData={activeCompany}
          search={search}
          onSelectAccount={onSelectAccount}
        />
      )}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────
export function PageCrmAccounts({ onSelectAccount, myPerms = [], userEmail = "" }) {
  const canManage = myPerms.includes("CRM_MANAGE");

  const [salespersons, setSalespersons] = useState([]);
  const [allAccounts,  setAllAccounts]  = useState([]);
  const [allCustomers, setAllCustomers] = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [activeTab,    setActiveTab]    = useState(null);
  const [search,       setSearch]       = useState("");

  useEffect(() => {
    Promise.all([
      api.fetchCrmCustomers(),
      api.fetchCrmAccounts(),
    ])
      .then(([customers, accounts]) => {
        const visible = canManage ? customers : customers.filter(sp => sp.userEmail === userEmail);
        setSalespersons(visible);
        setAllCustomers(customers);
        setAllAccounts(accounts);
        if (visible.length > 0) setActiveTab(visible[0].userEmail);
        else setActiveTab("PROSPECTS");
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line

  const lookupData = useMemo(() =>
    allCustomers.flatMap(sp =>
      sp.companies.flatMap(co =>
        (co.customers || []).map(c => ({
          ledgerName:   c.ledgerName,
          businessUnit: BU_STYLE[co.businessUnit]?.label ?? co.businessUnit,
          salesperson:  sp.userName,
        }))
      )
    ),
    [allCustomers]
  );

  const prospects = useMemo(() =>
    allAccounts.filter(a => a.status === "PROSPECT"), [allAccounts]);

  const totalCustomers = useMemo(() =>
    salespersons.reduce((sum, sp) =>
      sum + sp.companies.reduce((s2, c) => s2 + c.customers.length, 0), 0),
    [salespersons]);

  const activeSp = salespersons.find(sp => sp.userEmail === activeTab);

  return (
    <>
      <div className="page-header">
        <div className="page-header-left">
          <div className="page-title">CRM Accounts</div>
          <div className="page-subtitle">
            {loading ? "Loading…" : `${totalCustomers} customers · ${prospects.length} prospects`}
          </div>
        </div>
      </div>

      {/* Salesperson tabs */}
      <div style={{ display: "flex", gap: 0, borderBottom: "2px solid #e5e7eb", overflowX: "auto" }}>
        {salespersons.map(sp => {
          const count = sp.companies.reduce((s, c) => s + c.customers.length, 0);
          const active = activeTab === sp.userEmail;
          return (
            <button key={sp.userEmail}
              onClick={() => { setActiveTab(sp.userEmail); setSearch(""); }}
              style={{
                background: "none", border: "none", cursor: "pointer",
                padding: "10px 18px", fontSize: 13, fontWeight: 600, whiteSpace: "nowrap",
                color: active ? "#1d4ed8" : "#6b7280",
                borderBottom: active ? "2px solid #1d4ed8" : "2px solid transparent",
                marginBottom: -2,
              }}>
              {sp.userName}
              <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 400,
                color: active ? "#1d4ed8" : "#9ca3af" }}>
                ({count})
              </span>
            </button>
          );
        })}
        <button
          onClick={() => { setActiveTab("PROSPECTS"); setSearch(""); }}
          style={{
            background: "none", border: "none", cursor: "pointer",
            padding: "10px 18px", fontSize: 13, fontWeight: 600, whiteSpace: "nowrap",
            color: activeTab === "PROSPECTS" ? "#374151" : "#6b7280",
            borderBottom: activeTab === "PROSPECTS" ? "2px solid #374151" : "2px solid transparent",
            marginBottom: -2,
          }}>
          Prospects
          <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 400,
            color: activeTab === "PROSPECTS" ? "#374151" : "#9ca3af" }}>
            ({prospects.length})
          </span>
        </button>
        <button
          onClick={() => { setActiveTab("LOOKUP"); setSearch(""); }}
          style={{
            background: "none", border: "none", cursor: "pointer",
            padding: "10px 18px", fontSize: 13, fontWeight: 600, whiteSpace: "nowrap",
            color: activeTab === "LOOKUP" ? "#7c3aed" : "#6b7280",
            borderBottom: activeTab === "LOOKUP" ? "2px solid #7c3aed" : "2px solid transparent",
            marginBottom: -2,
          }}>
          Customer Lookup
        </button>
      </div>

      {/* Search */}
      <div className="action-bar" style={{ marginTop: 12 }}>
        <input className="form-input" style={{ width: 280 }}
          placeholder="Search customers…"
          value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      {error && <div className="alert-warn" style={{ padding: 10, marginBottom: 12 }}>{error}</div>}

      {loading && (
        <div style={{ textAlign: "center", color: "#9ca3af", padding: 40 }}>Loading…</div>
      )}

      {!loading && activeTab === "PROSPECTS" && (
        <ProspectsSection
          prospects={prospects}
          search={search}
          onSelectAccount={onSelectAccount}
        />
      )}

      {!loading && activeSp && (
        <SalespersonPanel
          key={activeSp.userEmail}
          sp={activeSp}
          search={search}
          onSelectAccount={onSelectAccount}
        />
      )}

      {!loading && activeTab === "LOOKUP" && (
        <div className="card" style={{ marginTop: 0 }}>
          <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 12 }}>
            Search any customer name to see which salesperson handles them.
          </div>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Customer (Tally Ledger)</th>
                  <th>Company</th>
                  <th>Salesperson</th>
                </tr>
              </thead>
              <tbody>
                {lookupData
                  .filter(r => !search.trim() || r.ledgerName.toLowerCase().includes(search.trim().toLowerCase()))
                  .sort((a, b) => a.ledgerName.localeCompare(b.ledgerName))
                  .map((r, i) => (
                    <tr key={i}>
                      <td className="fw-600">{r.ledgerName}</td>
                      <td style={{ fontSize: 11 }}>{r.businessUnit}</td>
                      <td style={{ fontSize: 12, color: "#374151" }}>{r.salesperson}</td>
                    </tr>
                  ))}
                {lookupData.filter(r => !search.trim() || r.ledgerName.toLowerCase().includes(search.trim().toLowerCase())).length === 0 && (
                  <tr>
                    <td colSpan={3} style={{ textAlign: "center", color: "#9ca3af", padding: 24 }}>
                      {search.trim() ? `No customer matching "${search}"` : "No customers found."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!loading && salespersons.length === 0 && activeTab !== "PROSPECTS" && (
        <div style={{ textAlign: "center", color: "#9ca3af", padding: 40 }}>
          No salesperson groups assigned yet. Go to Admin → Salesperson Groups to set up.
        </div>
      )}
    </>
  );
}
